import EngineeringIcon from '@mui/icons-material/Engineering';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  Link as MuiLink,
  Stack,
  Typography,
  alpha,
  Tooltip,
} from '@mui/material';
import Coordinates from 'components/Coordinates';
import { getFormattedRuPhone } from 'core/utils/commonUtils';
import { getGeolocationLink } from 'core/utils/orderUtils';
import { isEmpty, isNil } from 'lodash';
import { useMemo, type FC } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { getActionColor, getActionUserRoleName } from './utils';
import type { EventExtendedProps } from './CalendarPage';

interface EventCardProps {
  event: EventExtendedProps;
  onClick?: () => void;
}

// Reusable info item component for consistent styling
const InfoItem: FC<{
  icon: React.ReactNode;
  tooltip: string;
  children: React.ReactNode;
}> = ({ icon, tooltip, children }) => (
  <Stack direction='row' spacing={1} alignItems='flex-start'>
    <Tooltip title={tooltip}>
      <>{icon}</>
    </Tooltip>
    <Box sx={{ flex: 1 }}>{children}</Box>
  </Stack>
);

const EventCard: FC<EventCardProps> = ({ event, onClick }) => {
  const orderPath = useMemo(() => {
    const paths = ['/requests', '/orders', '/archive'] as const;
    return `${paths[event.orderState]}/${event.orderId}`;
  }, [event]);

  const actionColor = getActionColor(event.actionCode);

  return (
    <Card
      component={'li'}
      variant='outlined'
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': {
          boxShadow: onClick ? 2 : 0,
          transform: onClick ? 'translateY(-2px)' : 'none',
          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        },
        position: 'relative',
        overflow: 'hidden',
        '&:before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '4px',
          height: '100%',
          backgroundColor: actionColor,
        },
      }}
      onClick={onClick}>
      <CardContent>
        {/* Header with order number and action chip */}
        <Stack mb={2} direction={'row'} alignItems={'center'} gap={1}>
          <Typography variant='h6' component='div'>
            Заказ&nbsp;
            <MuiLink component={RouterLink} to={orderPath}>
              {event.orderNumber}
            </MuiLink>
          </Typography>

          <Chip
            label={event.action}
            size='small'
            sx={{
              backgroundColor: alpha(actionColor, 0.1),
              color: actionColor,
              fontWeight: 'medium',
            }}
          />
        </Stack>

        <Divider sx={{ mb: 2 }} />

        {/* Event details grid */}
        <Grid container spacing={2}>
          {/* Client */}
          <Grid item xs={12} sm={6}>
            <InfoItem
              icon={<PersonIcon fontSize='small' color='action' />}
              tooltip='Клиент'>
              <MuiLink component={RouterLink} to={`/clients/${event.clientId}`}>
                {event.clientName}
              </MuiLink>
            </InfoItem>
          </Grid>

          {/* Phone */}
          <Grid item xs={12} sm={6}>
            <InfoItem
              icon={<PhoneIcon fontSize='small' color='action' />}
              tooltip='Телефон'>
              {isEmpty(event.clientPhone) ? (
                <div>—</div>
              ) : (
                <MuiLink href={`tel:+${event.clientPhone}`}>
                  {getFormattedRuPhone(event.clientPhone)}
                </MuiLink>
              )}
            </InfoItem>
          </Grid>

          {/* Address */}
          {!isEmpty(event.orderAddress) && (
            <Grid item xs={12} sm={6}>
              <InfoItem
                icon={<LocationOnIcon fontSize='small' color='action' />}
                tooltip='Адрес'>
                <Typography variant='body2' fontSize={'smaller'}>
                  <div>{event.orderAddress}</div>
                  {!isNil(event.orderGeolocation) && !isEmpty(event.orderGeolocation) && (
                    <Box>
                      <Coordinates data={event.orderGeolocation} />
                      <IconButton
                        aria-label='Open map in new tab'
                        target='_blank'
                        size='small'
                        href={getGeolocationLink(event.orderGeolocation)}>
                        <OpenInNewIcon fontSize='inherit' />
                      </IconButton>
                    </Box>
                  )}
                </Typography>
              </InfoItem>
            </Grid>
          )}

          {/* Responsible user */}
          {!isNil(event.userId) && (
            <Grid item xs={12} sm={6}>
              <InfoItem
                icon={<EngineeringIcon fontSize='small' color='action' />}
                tooltip={getActionUserRoleName(event.actionCode)}>
                <MuiLink component={RouterLink} to={`/users/${event.userId}`}>
                  {event.userFullName}
                </MuiLink>
              </InfoItem>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default EventCard;
